import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Sparkles, Mail } from "lucide-react";
import { T, MANROPE, FOCUS } from "../clientsTokens";
import NeedsAttention from "../NeedsAttention";
import { ClientFormDialog, TouchDialog } from "../ClientDialogs";
import RequestApprovalDialog from "../../RequestApprovalDialog";
import { DotsMenu, Tag, Card, CardHead, Secondary } from "./recordBits";
import { ActivityCard } from "./ActivityCard";
import { RunbookCard } from "./RunbookCard";
import { ContactsCard, QuotesCard, JourneyCard, TicketsCard, RecordCard } from "./RailCards";
import BillingCard from "./BillingCard";
import { HealthCheckCard } from "./HealthCheckCard";
import { MeetingNotesDialog } from "./MeetingNotesDialog";
import { HubspotImportDialog } from "./HubspotImportDialog";
import { EntityTodosCard } from "../../todos/EntityTodosCard";
import { OverseasBadge } from "../../OverseasBadge";
import { AUTHORITY_OPTIONS } from "../../ContactsEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CS = `${API}/customer-success`;
const JIRA = "https://equipr.atlassian.net";
const today = () => new Date().toISOString().slice(0, 10);
const full = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ").trim();

const ContactDialog = ({ clientId, contact, onClose, onSaved }) => {
  const [f, setF] = useState({ first_name: "", last_name: "", role: "", email: "", phone_office: "", phone_mobile: "", authority: "", ...(contact || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const body = { first_name: f.first_name, last_name: f.last_name, role: f.role,
      email: f.email, phone_office: f.phone_office, phone_mobile: f.phone_mobile,
      authority: f.authority || "" };
    try {
      const base = `${CS}/clients/${encodeURIComponent(clientId)}/contacts`;
      if (contact?.id) await axios.put(`${base}/${contact.id}`, body);
      else await axios.post(base, body);
      toast.success(contact?.id ? "Contact updated" : "Contact added");
      await onSaved(); onClose();
    } catch { toast.error("Could not save that contact"); }
    finally { setBusy(false); }
  };
  const field = (k, label) => (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted }}>{label}</div>
      <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-field-${k}`}
        className={`w-full h-9 rounded-[7px] px-2.5 text-[13px] mt-1 bg-white ${FOCUS}`}
        style={{ border: `1px solid ${T.control}`, color: T.ink }} />
    </div>
  );
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 py-20" data-testid="contact-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.3)" }} onClick={onClose} />
      <div className="relative w-full max-w-[460px] rounded-[12px] overflow-hidden"
        style={{ background: T.card, border: `1px solid ${T.border}` }}>
        <div className="px-5 py-4" style={{ borderBottom: `1px solid ${T.dividerInner}` }}>
          <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: T.ink }}>
            {contact?.id ? "Edit contact" : "Add a contact"}
          </div>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-3">
          {field("first_name", "First name")}{field("last_name", "Last name")}
          {field("role", "Role")}{field("email", "Email")}
          {field("phone_office", "Office phone")}{field("phone_mobile", "Mobile")}
          <div className="col-span-2">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted }}>Authority</div>
            <select value={f.authority || ""} data-testid="contact-field-authority"
              onChange={(e) => setF({ ...f, authority: e.target.value })}
              className={`w-full h-9 rounded-[7px] px-2 text-[13px] mt-1 bg-white ${FOCUS}`}
              style={{ border: `1px solid ${T.control}`, color: T.ink }}>
              <option value="">Not set</option>
              {AUTHORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="text-[11.5px] mt-1" style={{ color: T.ink2 }}>
              {AUTHORITY_OPTIONS.find((o) => o.value === f.authority)?.desc
                || "What they can decide — who signs off, who holds the budget, who champions us."}
            </div>
          </div>
        </div>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: T.panel, borderTop: `1px solid ${T.dividerInner}` }}>
          <button onClick={onClose} className="h-9 px-3 text-[13px]" style={{ color: T.ink2 }}>Cancel</button>
          <button onClick={save} disabled={busy} data-testid="contact-save"
            className="ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase disabled:opacity-60"
            style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: "#000" }}>
            {busy ? "Saving…" : "Save contact"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* Clients directive Part B — the client record view. */
export default function ClientRecordPage({ recordId: idProp }) {
  const params = useParams();
  const recordId = idProp || params.recordId || params.id;
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [checks, setChecks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [touches, setTouches] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [journey, setJourney] = useState(null);
  const [runbook, setRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [naKey, setNaKey] = useState(0);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [hsOpen, setHsOpen] = useState(false);
  const [touchOpen, setTouchOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [contactDlg, setContactDlg] = useState(null);

  const cid = encodeURIComponent(recordId);
  const load = useCallback(async () => {
    try {
      const [cl, ct, hc, mt, tc, qt, jn, rb] = await Promise.all([
        axios.get(`${CS}/clients/${cid}`),
        axios.get(`${CS}/clients/${cid}/contacts`).catch(() => ({ data: {} })),
        axios.get(`${CS}/health-checks`, { params: { client_id: recordId } }).catch(() => ({ data: {} })),
        axios.get(`${CS}/clients/${cid}/meetings`).catch(() => ({ data: {} })),
        axios.get(`${CS}/clients/${cid}/touches`).catch(() => ({ data: {} })),
        axios.get(`${API}/quotes`, { params: { link_type: "client", link_id: recordId } }).catch(() => ({ data: {} })),
        axios.get(`${CS}/journeys/${cid}`).catch(() => ({ data: {} })),
        axios.get(`${API}/runbooks/${cid}`).catch(() => ({ data: null })),
      ]);
      setClient(cl.data.client || { client_id: recordId, company_name: recordId });
      setContacts((ct.data.contacts || []).map((c) => ({ ...c, name: full(c) })));
      setChecks(hc.data.health_checks || []);
      setMeetings(mt.data.meetings || []);
      setTouches(tc.data.touches || tc.data.activities || []);
      setQuotes(qt.data.quotes || []);
      setJourney(jn.data.journey || null);
      setRunbook(rb.data || null);
      setNaKey((k) => k + 1);
    } catch {
      toast.error("Could not load that client");
    } finally { setLoading(false); }
  }, [cid, recordId]);

  useEffect(() => { load(); }, [load]);

  const activity = useMemo(() => {
    const items = [];
    touches.forEach((a, i) => {
      const t = (a.type || "note").toLowerCase();
      const kind = ["email", "call", "note", "meeting", "task"].includes(t) ? t : "note";
      items.push({ id: `t-${a.id || i}`, src: "touch", srcId: a.id, kind,
        title: a.outcome || a.subject || "Activity",
        subtitle: a.rep ? `by ${a.rep}` : null, body: a.notes || "",
        source: a.source || null, date: a.activity_date || a.created_at });
    });
    meetings.forEach((m, i) => items.push({ id: `m-${m.id || i}`, src: "meeting", srcId: m.id, kind: "meeting",
      title: m.title || "Meeting", subtitle: m.attendees || null, actionItems: m.action_items || [],
      body: m.summary || m.raw_notes || "", date: m.meeting_date || m.created_at }));
    checks.forEach((h, i) => items.push({ id: `h-${h.id || i}`, src: "check", srcId: h.id, kind: "note",
      title: h.health_check_submission_id
        ? `${h.hc_mode === "client" ? "Client" : "Staff"} Health Check — ${h.hc_score}/100 · ${h.hc_band}`
        : `Health check — ${h.health_score ?? "?"}/10${h.sentiment ? ` · ${h.sentiment}` : ""}`,
      meta: h.health_check_submission_id ? { submission_id: h.health_check_submission_id } : null,
      subtitle: h.staff_name ? `by ${h.staff_name}` : null, body: h.notes || "", date: h.check_date }));
    return items.filter((i) => i.date).sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [touches, meetings, checks]);

  // An inactive contact has left, so the header never falls back to one.
  const liveContacts = contacts.filter((c) => c.active !== false);
  const primary = liveContacts.find((c) => c.is_primary)
    || liveContacts.find((c) => c.name && c.name === client?.contact_name)
    || liveContacts.find((c) => c.name)
    || (client?.contact_name ? { name: client.contact_name } : null);
  const title = client?.legal_name || client?.company_name || recordId;
  const c = client || { client_id: recordId };
  const csm = client?.csm && client.csm !== "Unassigned" ? client.csm : client?.csm_owner_id || null;

  const deleteActivity = async (it) => {
    const path = it.src === "touch" ? `${CS}/clients/${cid}/touches/${it.srcId}`
      : it.src === "meeting" ? `${CS}/clients/${cid}/meetings/${it.srcId}`
        : it.src === "check" ? `${CS}/health-checks/${it.srcId}` : null;
    if (!path) { toast.error("That activity can't be deleted"); return; }
    try {
      await axios.delete(path);
      toast.success("Activity deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete that activity"); }
  };

  /* An action item only matters once someone owns it with a date — promote it
     to a real to-do so it lands in that person's My Work. */
  const makeTodoFromAction = async (meetingId, index, ai) => {
    try {
      const r = await axios.post(`${API}/todos/from-action-item`, {
        link_type: "client", link_id: cid, link_name: client?.company_name || cid,
        meeting_id: meetingId, index, text: ai.text, owner: ai.owner || null,
      });
      const owner = (r.data.todo?.assignee || {}).name || r.data.todo?.owner_name;
      toast.success(r.data.created ? `To-do added${owner ? ` for ${owner}` : ""}`
        : "That action item already has a to-do");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create that to-do"); }
  };


  const markInactive = async () => {
    try {
      await axios.put(`${CS}/clients/${cid}`, { active: false });
      toast.success(`${title} is now inactive`);
      nav("/customer-success/clients");
    } catch { toast.error("Could not mark that client inactive"); }
  };

  const startJourney = async () => {
    try {
      await axios.put(`${CS}/journeys/${cid}`, { company_name: title, status: "onboarding" });
      toast.success("Journey started"); load();
    } catch { toast.error("Could not start the journey"); }
  };

  // The runbook is Allocate-specific — server, remote access, Allocate login.
  // Entitlements come from HubSpot licence counts, which is what the register
  // shows; the legacy Excel licence numbers inside the runbook itself are not
  // used here, since stale ones would keep the card on clients who left Allocate.
  const runsAllocate = (c.products || []).includes("EA") || Boolean(c.allocate_version);

  return (
    <div className="px-6 pt-4 pb-7" style={{ background: T.page, minHeight: "100%" }} data-testid="client-record">
      <div className="text-[11.5px] mb-2.5">
        <a href="/customer-success" style={{ color: T.accentInkPage }}>Customer Success</a>
        <span style={{ color: T.disabled }}> / </span>
        <a href="/customer-success/clients" style={{ color: T.accentInkPage }}>Clients</a>
        <span style={{ color: T.disabled }}> / </span>
        <span style={{ color: T.ink, fontWeight: 600 }} data-testid="crumb-client">{title}</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-3.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, letterSpacing: "-0.018em", color: T.ink }}
              data-testid="client-title">{client ? title : recordId}</h1>
            <OverseasBadge currency={c.currency} country={c.country} testid="client-record-overseas" />
            {c.is_vip && <Tag>VIP</Tag>}
            {c.has_sla && <Tag>SLA</Tag>}
            {c.active === false && <Tag>Inactive</Tag>}
          </div>
          <div className="text-[13px] mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: T.ink2 }}
            data-testid="client-identity">
            <span className="mono text-[11.5px]" style={{ color: T.ink }}>{c.client_id}</span>
            {c.jira_org_name && (<>
              <span style={{ color: T.disabled }}>·</span><span>Jira org</span>
              <span className="mono text-[11.5px]" style={{ color: T.ink }}>{c.jira_org_name}</span>
            </>)}
            <span style={{ color: T.disabled }}>·</span><span>Primary contact</span>
            <span style={{ color: T.ink, fontWeight: 600 }}>{primary?.name || primary?.email || "not set"}</span>
            <span style={{ color: T.disabled }}>·</span><span>CSM</span>
            <span style={{ color: T.ink, fontWeight: 600 }}>{csm || "Unassigned"}</span>
          </div>
        </div>

        <div className="flex flex-col sm:items-end gap-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => nav(`/health-check/${cid}/staff`)} data-testid="record-health-check"
              className="h-[34px] px-3.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase whitespace-nowrap"
              style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: "#000", letterSpacing: "0.04em" }}>
              <Heart className="w-3.5 h-3.5" /> Run a Health Check
            </button>
            <button onClick={() => nav(`/clients/${cid}/email`)} data-testid="record-send-email"
              className="h-[34px] px-3.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase whitespace-nowrap"
              style={{ background: T.card, border: `1px solid ${T.accentEdge}`, color: T.ink, letterSpacing: "0.04em" }}>
              <Mail className="w-3.5 h-3.5" /> Send email
            </button>
            <button onClick={() => setTouchOpen(true)} data-testid="log-a-touch"
              className="h-[34px] px-3.5 rounded-[7px] text-[12px] font-bold uppercase whitespace-nowrap"
              style={{ background: T.card, border: `1px solid ${T.control}`, color: T.ink2, letterSpacing: "0.04em" }}>
              Log a touch
            </button>
            <button onClick={() => setMeetingOpen(true)} data-testid="add-meeting-notes"
              className="h-[34px] px-3.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase whitespace-nowrap"
              style={{ background: T.card, border: `1px solid ${T.control}`, color: T.ink2, letterSpacing: "0.04em" }}>
              <Sparkles className="w-3.5 h-3.5" /> Add meeting notes
            </button>
            <DotsMenu testid="record-menu" size={34} items={[
              { label: "Add meeting notes", hint: "Paste raw Teams notes and we structure them.",
                onClick: () => setMeetingOpen(true), testid: "menu-add-meeting" },
              { label: "Import activity from HubSpot", hint: "Calls, meetings, emails and notes from HubSpot, added to this client's activity.",
                onClick: () => setHsOpen(true), testid: "menu-hubspot-import" },
              { label: "Request approval", hint: "Sends an approval request to the account owner.",
                onClick: () => setApprovalOpen(true), testid: "menu-request-approval" },
              { label: "Edit client", onClick: () => setEditOpen(true), testid: "menu-edit-client" },
              { label: "Open in Jira", testid: "menu-open-jira",
                onClick: () => window.open(`${JIRA}/jira/servicedesk/projects?q=${encodeURIComponent(c.jira_org_name || "")}`, "_blank") },
              { label: "Copy link", testid: "menu-copy-link",
                onClick: () => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); } },
              { label: "Mark inactive", danger: true, testid: "menu-mark-inactive",
                hint: "Hides the client from the register and stops check reminders. Reversible from Show inactive.",
                onClick: markInactive },
            ]} />
          </div>
          <div className="text-[11.5px] sm:text-right max-w-[340px]" style={{ color: T.ink2 }}>
            Fill the questionnaire in with them on a call. It scores out of 100, sets the next
            check date and updates the Clients register.
          </div>
        </div>
      </div>

      <NeedsAttention clientId={recordId} refreshKey={naKey} />

      <div className="grid gap-3.5 mt-3.5 items-start grid-cols-1 xl:grid-cols-[minmax(0,1.62fr)_minmax(0,1fr)]">
        <div className="space-y-3.5 min-w-0">
          <ActivityCard items={activity} loading={loading} onDelete={deleteActivity}
            onMakeTodo={makeTodoFromAction} />
          {runsAllocate && (
            <RunbookCard clientId={recordId} companyName={c.company_name} runbook={runbook} loading={loading} />
          )}
        </div>
        <div className="space-y-3.5 min-w-0">
          <ContactsCard clientId={recordId} contacts={contacts} onChanged={load}
            onAdd={() => setContactDlg({})} onEdit={(c) => setContactDlg(c)} />
          <HealthCheckCard clientId={recordId} companyName={c.company_name} />
          <EntityTodosCard linkType="client" linkId={recordId} linkName={c.company_name}
            refreshKey={naKey} onRecordCheck={() => nav(`/health-check/${cid}/staff`)} testid="client-todos" />
          <BillingCard clientId={recordId} currency={(c.currency || "AUD").toUpperCase()} />
          <QuotesCard clientId={recordId} quotes={quotes} onChanged={load} />
          <JourneyCard clientId={recordId} journey={journey} onStart={startJourney} />
          <TicketsCard jiraOrg={c.jira_org_name} openCount={c.open_ticket_count} />
          <RecordCard client={c} onEdit={() => setEditOpen(true)} />
        </div>
      </div>

      {meetingOpen && <MeetingNotesDialog client={c} today={today()} onClose={() => setMeetingOpen(false)} onSaved={load} />}
      {hsOpen && <HubspotImportDialog client={c} onClose={() => setHsOpen(false)} onImported={load} />}
      {contactDlg && <ContactDialog clientId={recordId} contact={contactDlg.id ? contactDlg : null}
        onClose={() => setContactDlg(null)} onSaved={load} />}
      <TouchDialog open={touchOpen} onOpenChange={setTouchOpen} clients={[c]} onSaved={load} today={today()} />
      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />
      <RequestApprovalDialog open={approvalOpen} onOpenChange={setApprovalOpen}
        record={{ type: "client", id: c.client_id, name: title }} />
    </div>
  );
}
